import React, { useState, useMemo } from "react";
import { Table, TableBody, TableHead, TableRow, TablePagination, InputBase } from "@mui/material";
import { TableCellCondensed, StyledTableContainer, TableFooterStyled } from "./index.styles";
import { Font12pxBold, Font12pxRegular, Font14pxBold, SearchBarBox, SearchFilterButton } from "../../../views/ProjectManagement/Program/Projects/DonationDetails/DonationDetail.styles";
import DownloadCSVButton from "components/common/DownloadCSVButton";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

export interface Transaction {
  id: number;
  donor_id: number;
  donation_amount: number;
  donation_date: string;
  donation_status: string;
  donor_name: string;
  donor_email: string;
}

interface TransactionsTableProps {
  transactions: Transaction[];
}

const TransactionsTable: React.FC<TransactionsTableProps> = ({ transactions }) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const filteredTransactions = useMemo(() => {
    if (!search.trim()) return transactions;
    const lower = search.toLowerCase();
    return transactions.filter(tx =>
      tx.donor_name.toLowerCase().includes(lower) ||
      tx.donor_email.toLowerCase().includes(lower) ||
      new Date(tx.donation_date).toLocaleDateString("en-US").includes(lower)
    );
  }, [transactions, search]);

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page if filter changes and current page is out of range
  React.useEffect(() => {
    if (page > 0 && page * rowsPerPage >= filteredTransactions.length) {
      setPage(0);
    }
  }, [filteredTransactions.length, page, rowsPerPage]);

  return (
    <div style={{ width: '100%' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        padding: '2rem 0.5rem 0.5rem 0',
        flexWrap: 'wrap',
        width: '100%',
      }}>
        <Font12pxBold style={{ fontSize: 22, color: '#111', minWidth: 120, margin: 0 }}>Transactions</Font12pxBold>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <SearchBarBox style={{ minWidth: 320, maxWidth: 480, width: '100%' }}>
            <SearchIcon color="action" />
            <InputBase
              placeholder="Search Users by Name, Email or Date"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ fontSize: "0.875rem", flex: 1, ml: '0.75rem' }}
            />
            <SearchFilterButton disabled>
              <FilterAltIcon color="action" sx={{ width: "1.125rem", height: "1.125rem", marginRight: "0.5rem" }} />
              Filter
            </SearchFilterButton>
          </SearchBarBox>
        </div>
      </div>
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ background: '#faf7fa' }}>
              <TableCellCondensed>
                <Font12pxBold style={{ color: '#fe2f67', letterSpacing: 1 }}>DATE</Font12pxBold>
              </TableCellCondensed>
              <TableCellCondensed>
                <Font12pxBold style={{ color: '#fe2f67', letterSpacing: 1 }}>DONOR</Font12pxBold>
              </TableCellCondensed>
              <TableCellCondensed>
                <Font12pxBold style={{ color: '#fe2f67', letterSpacing: 1 }}>STATUS</Font12pxBold>
              </TableCellCondensed>
              <TableCellCondensed>
                <Font12pxBold style={{ color: '#fe2f67', letterSpacing: 1 }}>AMOUNT</Font12pxBold>
              </TableCellCondensed>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTransactions
              .slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              .map((donation, index) => (
                <TableRow key={index}>
                  <TableCellCondensed>
                    <Font14pxBold>
                      {new Date(
                        donation.donation_date
                      ).toLocaleDateString("en-US", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </Font14pxBold>
                    <Font12pxRegular style={{ color: "gray" }}>
                      {new Date(
                        donation.donation_date
                      ).toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Font12pxRegular>
                  </TableCellCondensed>
                  <TableCellCondensed>
                    <Font14pxBold>
                      {donation.donor_name}
                    </Font14pxBold>
                    <Font12pxRegular style={{ color: "gray" }}>
                      {donation.donor_email}
                    </Font12pxRegular>
                  </TableCellCondensed>
                  <TableCellCondensed>
                    <span style={{
                      display: 'inline-block',
                      background: donation.donation_status === 'success' ? '#e6f9ed' : '#ffe3ef',
                      color: donation.donation_status === 'success' ? '#1bb273' : '#fe2f67',
                      borderRadius: 12,
                      padding: '2px 12px',
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: 0.5,
                      minWidth: 70,
                      textAlign: 'center',
                    }}>
                      {donation.donation_status}
                    </span>
                  </TableCellCondensed>
                  <TableCellCondensed>
                    <Font14pxBold style={{ color: "#fe2f67", display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 17 }}>$</span>{donation.donation_amount}
                    </Font14pxBold>
                    <Font12pxRegular style={{ color: "gray" }}>
                      {"USD"}
                    </Font12pxRegular>
                  </TableCellCondensed>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TableFooterStyled>
          <DownloadCSVButton
            data={filteredTransactions.map(tx => ({
              date: new Date(tx.donation_date).toLocaleDateString("en-US"),
              time: new Date(tx.donation_date).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
              donor: tx.donor_name,
              email: tx.donor_email,
              status: tx.donation_status,
              amount: tx.donation_amount,
            }))}
            headers={["date", "time", "donor", "email", "status", "amount"]}
            filename="transactions.csv"
            label="Download - CSV.format"
          />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableFooterStyled>
      </StyledTableContainer>
    </div>
  );
};

export default TransactionsTable; 